import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { showToast } from "../../utils/toastUtils";
import "./NewPassword.css";
import loginBgImage from "../../assets/images/Login_Page_bg.png";
import logoImage from "../../assets/images/AstrocureLogo.svg";
import { useNavigate } from "react-router-dom";
import { callApi } from "../../utils/apiService";
import { videoCreatorEndPoint } from "../../Constants/constants";
import { useUser } from "../../Context/UserContext";

const NewPassword = () => {
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const { passwordReset, clearPasswordResetData } = useUser();

  // Check if email and resetToken exist in context
  useEffect(() => {
    if (!passwordReset.email || !passwordReset.resetToken) {
      showToast("error", "Please verify your OTP first");
      navigate("/forgot-password");
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validatePasswords = () => {
    const newErrors = {};

    // First, check new password length requirement
    if (!formData.newPassword.trim()) {
      newErrors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = "Password must be at least 8 characters";
    }

    // Only validate confirm password if new password meets 8 character requirement
    if (formData.newPassword.length >= 8) {
      if (!formData.confirmPassword.trim()) {
        newErrors.confirmPassword = "Confirm password is required";
      } else if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = "New Password and Confirm Password do not match";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validatePasswords()) {
      return;
    }

    setLoading(true);
    try {
      const response = await callApi(
        `${videoCreatorEndPoint}auth/reset-password`,
        "POST",
        {
          email: passwordReset.email,
          newPassword: formData.newPassword,
        },
        {
          Authorization: `Bearer ${passwordReset.resetToken}`,
        }
      );

      if (response.status) {
        // Show success toast
        showToast("success", "Password reset successfully", 100);

        // Clear password reset data from context
        clearPasswordResetData();

        // Navigate to success page
        setTimeout(() => {
          navigate("/success-page");
        }, 1000);
      } else {
        showToast("error", response.alert || response.message || "Password reset failed");
      }
    } catch (error) {
      showToast("error", error.message || "Password reset failed. Please try again.");
      console.error("Reset password error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* LEFT SIDE IMAGE */}
      <div
        className="login-left"
        style={{ backgroundImage: `url(${loginBgImage})` }}
      >
        <div className="mobile-wave">
          <svg
            className="mobile-wave-svg"
            viewBox="0 0 1440 320"
            preserveAspectRatio="none"
          >
            <path
              fill="#f5f5f5"
              d="M0,224L80,202.7C160,181,320,139,480,154.7C640,171,800,245,960,250.7C1120,256,1280,192,1360,160L1440,128L1440,320L1360,320C1280,320,1120,320,960,320C800,320,640,320,480,320C320,320,160,320,80,320L0,320Z"
            />
          </svg>
        </div>
      </div>

      {/* MID CURVE */}
      <div className="mid-curve">
        <svg
          className="mid-curve-svg"
          viewBox="0 0 520 1000"
          preserveAspectRatio="none"
        >
          <path
            d="
              M 520 0
              L 270 0
              C 220 70, 205 140, 245 210
              C 310 330, 175 430, 140 520
              C 100 640, 260 730, 275 840
              C 290 930, 225 1000, 170 1000
              L 520 1000
              Z
            "
            fill="#f5f5f5"
          />
        </svg>
      </div>

      {/* Right side */}
      <div className="login-right">
        <div className="login-card">
          <div className="login-logo">
            <img src={logoImage} alt="AstroCure" className="login-logo-image" />
          </div>

          <h1 className="login-title">Welcome To AstroCure!</h1>

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <div className="one-step-text">Set your new password</div>
              <div className="one-step-desc">
                Choose a new password to keep your account safe and secure.
              </div>

              {/* New Password */}
              <div className="floating-group">
                <label htmlFor="newPassword" className="floating-label">
                  Choose a new password <span className="required">*</span>
                </label>

                <div className="password-wrapper">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    id="newPassword"
                    name="newPassword"
                    className={`form-input floating-input ${errors.newPassword ? "form-input-error" : ""}`}
                    placeholder="Enter new password"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                  />

                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowNewPassword((prev) => !prev)}
                    aria-label={
                      showNewPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showNewPassword ? (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <line
                          x1="1"
                          y1="1"
                          x2="23"
                          y2="23"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle
                          cx="12"
                          cy="12"
                          r="3"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.newPassword && <p className="password-error">{errors.newPassword}</p>}
              </div>

              {/* Confirm Password */}
              <div className="floating-group">
                <label htmlFor="confirmPassword" className="floating-label">
                  Confirm Password <span className="required">*</span>
                </label>

                <div className="password-wrapper">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    className={`form-input floating-input ${errors.confirmPassword ? "form-input-error" : ""}`}
                    placeholder="Confirm new password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                  />

                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    aria-label={
                      showConfirmPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showConfirmPassword ? (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <line
                          x1="1"
                          y1="1"
                          x2="23"
                          y2="23"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle
                          cx="12"
                          cy="12"
                          r="3"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.confirmPassword && <p className="password-error">{errors.confirmPassword}</p>}
              </div>
            </div>

            <button type="submit" className="verify-code-button" >
              Save & Continue
            </button>
          </form>
        </div>

        <footer className="login-footer">
          Copyright © 2026 Astrovidhi Private Limited All rights reserved
        </footer>
      </div>
    </div>
  );
};

export default NewPassword;
